// src/pages/AddCompanyPage.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BackToDashboard from "../components/BackToDashboard";
import './UserManagementPage.css';

const API_BASE = (process.env.REACT_APP_API_BASE || 'https://app.kmaavfall.se/api').replace(/\/+$/, '');

export default function AddCompanyPage() {
  const navigate = useNavigate();

  const [companyId, setCompanyId] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  const [orgNumber, setOrgNumber] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [address, setAddress] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');

  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadAuth();
  }, []);

  async function loadAuth() {
    setAuthLoading(true);
    setMsg('');

    try {
      const res = await fetch(`${API_BASE}/auth/me`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: 'include',
      });

      const raw = await res.text();
      let data = null;

      try {
        data = raw ? JSON.parse(raw) : null;
      } catch {
        throw new Error(`HTTP ${res.status}`);
      }

      if (!res.ok || !data?.ok) {
        throw new Error(data?.error || 'Du verkar inte vara inloggad.');
      }

      const cid = Number(data?.company?.id || data?.user?.company_id || 0);

      if (!cid) {
        throw new Error('Kunde inte läsa company_id för inloggat konto.');
      }

      setCompanyId(cid);
    } catch (err) {
      console.error('[AddCompanyPage] auth load failed:', err);
      setMsg(err.message || 'Kunde inte läsa inloggat företag.');
    } finally {
      setAuthLoading(false);
    }
  }

  function resetForm() {
    setOrgNumber('');
    setCompanyName('');
    setAddress('');
    setPostalCode('');
    setCity('');
    setContactPerson('');
    setContactEmail('');
    setContactPhone('');
  }

  function normalizeOrgNumber(value) {
    return String(value || '').trim();
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setMsg('');

    if (!companyId) {
      setMsg('Kunde inte hitta inloggat företag. Ladda om sidan och försök igen.');
      return;
    }

    if (!companyName.trim()) {
      setMsg('Ange verksamhetens namn.');
      return;
    }

    if (!orgNumber.trim()) {
      setMsg('Ange organisationsnummer.');
      return;
    }

    if (
      contactEmail.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail.trim())
    ) {
      setMsg('Ange en giltig e-postadress för kontaktpersonen.');
      return;
    }

    setSaving(true);

    try {
      const payload = {
        companyId,
        name: companyName.trim(),
        org_number: normalizeOrgNumber(orgNumber),
        address: address.trim(),
        postal_code: postalCode.trim(),
        city: city.trim(),
        contact_person: contactPerson.trim(),
        contact_email: contactEmail.trim().toLowerCase(),
        phone: contactPhone.trim(),
        is_default: false,
      };

      const res = await fetch(`${API_BASE}/company-entities`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      const raw = await res.text();
      let data = null;

      try {
        data = raw ? JSON.parse(raw) : null;
      } catch {
        throw new Error(`HTTP ${res.status}`);
      }

      if (!res.ok || data?.error) {
        throw new Error(data?.error || `HTTP ${res.status}`);
      }

      setMsg('Bolaget har lagts till ✅');
      resetForm();
    } catch (err) {
      console.error('[AddCompanyPage] save failed:', err);
      setMsg(err.message || 'Kunde inte spara bolaget.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="user-mgmt-container">
      <div style={{ marginBottom: "1.2rem", display: "flex", justifyContent: "center" }}>
        <BackToDashboard label="← Tillbaka till Dashboard" />
      </div>

      <h2 className="user-mgmt-title">Lägg till bolag</h2>

      <p className="user-mgmt-instruction">
        Lägg till fler verksamhetsutövare som du senare kan välja mellan i grunduppgifter och på dokument.
      </p>

      <div style={{ marginBottom: 16 }}>
        <button
          type="button"
          className="btn-primary"
          onClick={() => navigate('/user-management')}
          disabled={saving || authLoading}
        >
          ← Tillbaka till användarhantering
        </button>
      </div>

      {msg && <div className="um-alert">{msg}</div>}

      <form onSubmit={handleSubmit} className="add-user-form">
        <h3>Nytt bolag / verksamhetsutövare</h3>

        <div className="form-group">
          <label htmlFor="orgNumber">Organisationsnummer</label>
          <input
            id="orgNumber"
            type="text"
            value={orgNumber}
            onChange={(e) => setOrgNumber(e.target.value)}
            placeholder="556123-4567"
            required
            disabled={saving || authLoading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="companyName">Verksamhetens namn</label>
          <input
            id="companyName"
            type="text"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            placeholder="Exempel AB"
            required
            disabled={saving || authLoading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="address">Gatuadress</label>
          <input
            id="address"
            type="text"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="Exempelgatan 1"
            disabled={saving || authLoading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="postalCode">Postnummer</label>
          <input
            id="postalCode"
            type="text"
            value={postalCode}
            onChange={(e) => setPostalCode(e.target.value)}
            placeholder="XXXXX"
            disabled={saving || authLoading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="city">Ort</label>
          <input
            id="city"
            type="text"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="Exempelstad"
            disabled={saving || authLoading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="contactPerson">Kontaktperson</label>
          <input
            id="contactPerson"
            type="text"
            value={contactPerson}
            onChange={(e) => setContactPerson(e.target.value)}
            placeholder="Förnamn Efternamn"
            disabled={saving || authLoading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="contactEmail">E-post (kontaktperson)</label>
          <input
            id="contactEmail"
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            placeholder="namn@bolag.se"
            disabled={saving || authLoading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="contactPhone">Telefon (kontaktperson)</label>
          <input
            id="contactPhone"
            type="text"
            value={contactPhone}
            onChange={(e) => setContactPhone(e.target.value)}
            placeholder="070-123 45 67"
            disabled={saving || authLoading}
          />
        </div>

        <button
          className="btn-primary"
          type="submit"
          disabled={saving || authLoading || !companyId}
        >
          {authLoading ? 'Laddar…' : saving ? 'Sparar…' : 'Spara bolag'}
        </button>
      </form>
    </div>
  );
}